import gsap from 'gsap';

export class MagneticCaption {
  constructor(common, elem) {
    this.common = common;
    this.elem = elem;
    this.circle = this.elem.querySelector('.b3-magnetic-caption__circle');
    this.line = this.elem.querySelector('.b3-magnetic-caption__line');
    this.content = this.elem.querySelector('.b3-magnetic-caption__content');
    this.title = this.elem.querySelector('.b3-magnetic-caption__title');
    this.text = this.elem.querySelector('.b3-magnetic-caption__text');
    this.positionX = Number(this.elem.getAttribute('data-x'));
    this.positionY = Number(this.elem.getAttribute('data-y'));
    this.reverseX = this.elem.getAttribute('data-reverse-x') === 'true';
    this.reverseY = this.elem.getAttribute('data-reverse-y') === 'true';
    this.isOpen = false;

    this.classNameActive = 'b3-magnetic-caption--active';

    this.init();
  }

  init() {
    if (!this.common.isMobile) {
      this.elem.addEventListener('mouseenter', () => {
        this.show();
      });
  
      this.elem.addEventListener('mousemove', (e) => {
        this.move(e);
      });
  
      this.elem.addEventListener('mouseleave', () => {
        this.hide();
      });
  
      window.addEventListener('mousemove', (e) => {
        this.moveWindow(e);
      });
    } else {
      let contentRect = this.content.getBoundingClientRect();

      this.circle.addEventListener('click', () => {
        if (!this.isOpen) {
          gsap.set(this.content, {
            x: () => {
              if (contentRect.right > window.innerWidth) {
                return window.innerWidth - contentRect.right - 12;
              } else {
                return 0;
              }
            },
          });

          this.show();
        } else {
          this.hide();
        }
      });

      window.addEventListener('click', (e) => {
        let isTarget = e.target === this.circle;
        if (this.isOpen && !isTarget) {
          this.hide();
        }
      });
      window.addEventListener('keydown', () => {
        if (this.isOpen) {
          this.hide();
        }
      });
      let prevWidth = window.innerWidth;
      window.addEventListener('resize', () => {
        const width = window.innerWidth;
        if (width !== prevWidth) {
          prevWidth = width;
          contentRect = this.content.getBoundingClientRect();

          if (this.isOpen) {
            this.hide();
          }
        }
      });
    }
  }
  
  show() {
    this.isOpen = true;
    this.elem.classList.add(this.classNameActive);
    
    gsap.to(this.line, {
      opacity: 1,
      duration: 0.4,
      ease: 'power1.inOut',
    });

    gsap.fromTo(this.content, {
      opacity: 0,
    }, {
      opacity: 1,
      duration: 0.6,
      ease: 'power1.inOut',
    });

    gsap.fromTo([this.title, this.text], {
      y: 5,
    }, {
      y: 0,
      duration: 0.6,
      ease: 'power1.inOut',
      stagger: 0.05,
    });
  }

  hide() {
    this.isOpen = false;
    this.elem.classList.remove(this.classNameActive);

    gsap.to(this.content, {
      opacity: 0,
      duration: 0.3,
      ease: 'power1.inOut',
      overwrite: true,
    });

    gsap.to([this.title, this.text], {
      y: 10,
      duration: 0.3,
      ease: 'power1.inOut',
      stagger: -0.05,
      overwrite: true,
    });

    gsap.to(this.circle, {
      scale: 1,
      duration: 0.4,
      ease: 'power1.inOut',
      overwrite: true,
    });

    gsap.to(this.line, {
      opacity: 0,
      scaleY: 0,
      duration: 0.3,
      ease: 'power1.inOut',
      overwrite: true,
    });
  }

  move(event) {
    const rect = event.target.getBoundingClientRect();
    const left = event.offsetX;
    const top = event.offsetY;
    const offsetFromCenterX = rect.width / 2 - left;
    const offsetFromCenterY = rect.height / 2 - top;
    const absOffsetFromCenterX = Math.abs(offsetFromCenterX);
    const absOffsetFromCenterY = Math.abs(offsetFromCenterY);
    const maxTranslate = 5;

    gsap.to(this.elem, {
      x: () => {
        let transtateX = offsetFromCenterX * maxTranslate / rect.width;
        transtateX = this.reverseX ? transtateX : -transtateX;
        return this.positionX + transtateX;
      },
      y: () => {
        let transtateY = offsetFromCenterY * maxTranslate / rect.height;
        transtateY = this.reverseX ? transtateY : -transtateY;
        return this.positionY + transtateY;
      },
      duration: 0.2,
      overwrite: true,
      ease: 'none',
    });

    gsap.to(this.content, {
      x: () => {
        const contentRect = this.content.getBoundingClientRect();
        const maxRight = event.clientX + contentRect.width;

        if (maxRight > window.innerWidth) {
          return left + (window.innerWidth - maxRight);
        } else {
          return left + 10;
        }
      },
      y: top + 15,
      duration: 0.2,
      ease: 'none',
    });

    gsap.set(this.line, {
      rotate: () => {
        const center = {
          x: rect.left + rect.width / 2,
          y: rect.top + rect.height / 2,
        };
        const angle = Math.atan2(event.pageX - center.x, - (event.pageY - center.y));
        return angle * 180 / Math.PI;
      },
    });

    gsap.to(this.line, {
      scaleY: () => {
        const minScale = 0.1;
        const offsetXPercent = Math.abs(absOffsetFromCenterX * 100 / rect.width);
        const offsetYPercent = Math.abs(absOffsetFromCenterY * 100 / rect.height);
        const scaleX = offsetXPercent / 100;
        const scaleY = offsetYPercent / 100;

        return Math.max(scaleX, scaleY, minScale);
      },
      duration: 0.2,
      ease: 'power1.inOut',
    });
  }

  moveWindow(event) {
    if (event.target !== this.elem) {
      const left = event.clientX;
      const top = event.clientY;
      const maxTranslate = 0.015;
  
      gsap.to(this.elem, {
        x: () => {
          let transtateX = left * maxTranslate;
          transtateX = this.reverseX ? transtateX : -transtateX;
          return this.positionX + transtateX;
        },
        y: () => {
          let transtateY = top * maxTranslate;
          transtateY = this.reverseY ? transtateY : -transtateY;
          return this.positionY + transtateY;
        },
        duration: 0.5,
        ease: 'none',
      });
    }
  }
}

export default class MagneticCaptionIterator {
  constructor(app) {
    this.magneticCaptions = document.querySelectorAll('.b3-magnetic-caption');

    if (this.magneticCaptions.length) {
      this.magneticCaptions.forEach(item => new MagneticCaption(app, item));
    }
  }
}